import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import {expenseActions} from '../../state/actions/expense.actions';
import {dateTypesConstants} from '../../constants';
import {expensesHelpers, tabHelpers} from '../../utils/helpers';
import {
  ALL_TAB_BASE_QUERY,
  ALL_TAB_TOTALS_REFRESH_DELAY_MS,
} from '../../components/pages/ExpensesPage/AllTab/allTab.constants';

const {SETTLEMENT_DATE} = dateTypesConstants;

const ALL_TAB_VIEW = 'all';
const SUMMARIZE_KEY_NAME = 'isMonthGrouping';

// The monthly grouping rows are running totals, which only mean anything while the list is in date
// order — over a price-ordered list they would sum whichever rows happened to land next to each
// other. Product ruled that totals are hidden whenever the list is sorted by anything but date
// (H1MVP-4213). The ordering itself is decided in TableFilters.buildApiFilter, which is shared by
// every tab; this only reads what the query already carries.
const isDateOrdered = (orderBy) => !orderBy || orderBy === 'date' || orderBy === '-date';

const getApiQuery = (query) => {
  const {month, year, date_field, ...rest} = query;
  const dateFilterValues = expensesHelpers.getExpenseListFilterValues({month, year, date_field});
  return {
    ...rest,
    ...(dateFilterValues ? dateFilterValues : {month, year}),
  };
};

const NO_TOTALS = {status: 'idle', refreshing: false, byMonth: {}};

export const useAllTab = ({initialQuery = ALL_TAB_BASE_QUERY} = {}) => {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [currentQuery, setCurrentQuery] = useState(initialQuery);
  const [totals, setTotals] = useState(NO_TOTALS);

  const rowsRef = useRef(rows);
  const loadedRef = useRef(false);
  const currentQueryRef = useRef(initialQuery);
  const paginationRef = useRef(null);
  const totalsRequestIdRef = useRef(0);
  const totalsTimerRef = useRef(null);
  const totalsRef = useRef(NO_TOTALS);
  const totalsDirtyRef = useRef(false);
  const totalsPendingRef = useRef(false);

  useEffect(() => {rowsRef.current = rows;}, [rows]);
  useEffect(() => {paginationRef.current = pagination;}, [pagination]);
  useEffect(() => {totalsRef.current = totals;}, [totals]);

  // The month totals come from their own endpoint and are not tied to the list's pages, so they are
  // fetched once per filter set and again whenever a row changes or more rows load. With no period
  // selected the range is taken from the loaded rows, so the caller passes the rows it has just loaded.
  // `silent` keeps the current figures on screen while refetching (months that have no figure yet show
  // a skeleton); otherwise every cell shows one. `merge` adds the response to the figures already held
  // instead of replacing them — used when only newly loaded months are being asked for. A response that is
  // no longer the latest request is dropped.
  const loadTotals = useCallback(({silent = false, merge = false, expenses = rowsRef.current} = {}) => {
    clearTimeout(totalsTimerRef.current);
    totalsDirtyRef.current = false;
    const requestId = ++totalsRequestIdRef.current;
    const query = currentQueryRef.current;

    // The totals endpoint only groups by creation or expense date, so under a settlement-date filter
    // its figures would not match the rows shown; hide them, as for a price-ordered list.
    if (!isDateOrdered(getApiQuery(query).order_by) || query.date_field === SETTLEMENT_DATE) {
      totalsPendingRef.current = false;
      setTotals(NO_TOTALS);
      return;
    }

    const totalsQuery = expensesHelpers.getMonthlySpendingQuery(query, {expenses});
    if (!totalsQuery) {
      totalsPendingRef.current = false;
      setTotals({status: 'ready', refreshing: false, byMonth: {}});
      return;
    }

    setTotals(prev => silent ? {...prev, refreshing: true} : {status: 'loading', refreshing: false, byMonth: {}});
    totalsPendingRef.current = true;

    dispatch(expenseActions.getExpensesMonthlySpending({
      query: totalsQuery,
      successCallback: (response) => {
        if (requestId !== totalsRequestIdRef.current) return;
        totalsPendingRef.current = false;
        if (!Array.isArray(response)) {
          setTotals({status: 'error', refreshing: false, byMonth: {}});
          return;
        }
        const fresh = Object.fromEntries(response.map(({date, total_spending}) => [date, total_spending]));
        setTotals(prev => ({
          status: 'ready',
          refreshing: false,
          byMonth: merge ? {...prev.byMonth, ...fresh} : fresh,
        }));
      },
      errorCallback: () => {
        if (requestId !== totalsRequestIdRef.current) return;
        totalsPendingRef.current = false;
        // A failed partial request only leaves the new months without a figure; the ones already shown
        // are still right.
        setTotals(prev => merge
          ? {...prev, refreshing: false}
          : {status: 'error', refreshing: false, byMonth: {}});
      },
    }));
  }, [dispatch]);

  const scheduleTotalsRefresh = useCallback(() => {
    if (!loadedRef.current) return;
    totalsDirtyRef.current = true;
    clearTimeout(totalsTimerRef.current);
    totalsTimerRef.current = setTimeout(() => loadTotals({silent: true}), ALL_TAB_TOTALS_REFRESH_DELAY_MS);
  }, [loadTotals]);

  useEffect(() => () => clearTimeout(totalsTimerRef.current), []);

  // Without a selected period the totals follow the list. A further page only adds months, and a month's
  // total can't change by loading more of its rows, so just the months with no figure yet are asked for
  // and merged in. That is only safe while the figures held are settled: if a refetch is pending or in
  // flight, a partial request would supersede it and leave stale figures, so everything is refetched.
  const loadTotalsAfterList = useCallback(({initial, skipLoading, expenses}) => {
    if (initial) {
      loadTotals({silent: skipLoading, expenses});
      return;
    }
    const canMerge = totalsRef.current.status === 'ready'
      && !totalsPendingRef.current
      && !totalsDirtyRef.current;
    if (!canMerge) {
      loadTotals({silent: true, expenses});
      return;
    }
    const missing = expenses.filter(expense => (
      !expense[SUMMARIZE_KEY_NAME]
      && !(expensesHelpers.getMonthlySpendingKey(expense.created_at) in totalsRef.current.byMonth)
    ));
    if (missing.length) loadTotals({silent: true, merge: true, expenses: missing});
  }, [loadTotals]);

  const loadExpenses = useCallback(({initial = false, queryOverride = null, skipLoading = false} = {}) => {
    const mergedQuery = {
      ...currentQueryRef.current,
      ...(queryOverride || {}),
      ...(initial ? {cursor: undefined} : {}),
    };

    currentQueryRef.current = mergedQuery;
    setCurrentQuery(mergedQuery);

    if (!skipLoading) setLoading(true);

    // With a month and year selected the totals' range is known up front, so they load alongside the
    // list. Without one the range comes from the loaded rows: they load once the list arrives (and
    // again on every further page, which can widen it).
    const hasPeriod = mergedQuery.month != null && mergedQuery.year != null;
    if (initial && hasPeriod) loadTotals({silent: skipLoading});
    if (initial && !hasPeriod) {
      clearTimeout(totalsTimerRef.current);
      totalsRequestIdRef.current += 1;
      totalsPendingRef.current = false;
      totalsDirtyRef.current = false;
      setTotals(prev => skipLoading
        ? {...prev, refreshing: false}
        : {status: 'loading', refreshing: false, byMonth: {}});
    }

    const apiQuery = getApiQuery(mergedQuery);
    const groupRows = (list) => isDateOrdered(apiQuery.order_by)
      ? expensesHelpers.getGroupedExpenses(list)
      : list;

    dispatch(expenseActions.getExpenses({
      query: apiQuery,
      successCallback: (response) => {
        response.results = groupRows(response.results);

        if (!initial) {
          const combined = [...rowsRef.current, ...response.results].filter(r => !r[SUMMARIZE_KEY_NAME]);
          response.results = groupRows(combined);
        }

        const paginationData = typeof response.pagination === 'string'
          ? {cursor: response.pagination}
          : response.pagination || null;

        rowsRef.current = response.results;
        setRows(response.results);
        setPagination(paginationData);
        loadedRef.current = true;
        setLoaded(true);
        if (!skipLoading) setLoading(false);
        if (!hasPeriod) loadTotalsAfterList({initial, skipLoading, expenses: response.results});
      },
      errorCallback: () => {
        if (!skipLoading) setLoading(false);
        if (!initial || hasPeriod) return;
        // The list is gone but the totals request it superseded was dropped, so settle them here.
        if (skipLoading) loadTotals({silent: true});
        else setTotals({status: 'error', refreshing: false, byMonth: {}});
      },
    }));
  }, [dispatch, loadTotals, loadTotalsAfterList]);

  useEffect(() => {
    loadExpenses({initial: true});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTableChange = useCallback((_pagination, _filters, sorter, extra) => {
    tabHelpers.handleOnTableChange({
      view: ALL_TAB_VIEW,
      sorter,
      extra,
      loadDataFunc: ({query}) => loadExpenses({initial: true, queryOverride: query}),
    });
  }, [loadExpenses]);

  const updateRows = useCallback((expenses) => {
    const byId = Object.fromEntries(expenses.map(e => [e.id, e]));
    setRows(prev => prev.map(row => byId[row.id] ?? row));
    scheduleTotalsRefresh();
  }, [scheduleTotalsRefresh]);

  const updateRow = useCallback(({id, response, patch}) => {
    scheduleTotalsRefresh();
    setRows(prev => {
      if (patch) {
        if (!prev.some(r => r.id === id)) return prev;
        const shouldRemove = currentQueryRef.current?.policy_status
          && String(patch.policy_status) !== String(currentQueryRef.current.policy_status);
        return shouldRemove
          ? prev.filter(r => r.id !== id)
          : prev.map(r => r.id === id ? {...r, ...patch} : r);
      }
      if (response === null || response === undefined) {
        return prev.filter(r => r.id !== id);
      }
      if (!prev.some(r => r.id === id)) return prev;
      const queryReviewStatus = String(currentQueryRef.current.review_status);
      const shouldRemove = currentQueryRef.current?.review_status
        && queryReviewStatus && String(response?.review_status) !== queryReviewStatus;
      return shouldRemove
        ? prev.filter(r => r.id !== id)
        : prev.map(r => r.id === id ? response : r);
    });
  }, [scheduleTotalsRefresh]);

  const refresh = useCallback(() => {
    if (loadedRef.current) loadExpenses({initial: true, skipLoading: true});
  }, [loadExpenses]);

  const filter = useCallback((filterQuery) => {
    loadExpenses({initial: true, queryOverride: filterQuery});
  }, [loadExpenses]);

  const loadExpense = useCallback((id, callback, isUpdate = true) => {
    dispatch(expenseActions.getExpense({
      id,
      successCallback: (expense) => {
        setRows(prev => {
          const exists = prev.some(r => r.id === id);
          if (isUpdate && exists) return prev.map(r => r.id === id ? expense : r);
          if (!isUpdate && !exists) return [...prev, expense];
          return prev;
        });
        scheduleTotalsRefresh();
        callback?.(expense);
      },
    }));
  }, [dispatch, scheduleTotalsRefresh]);

  const prependExpenses = useCallback((expenses) => {
    setRows(prev => {
      const flat = prev.filter(r => !r[SUMMARIZE_KEY_NAME]);
      const combined = [...expenses, ...flat];
      // Rows arriving later (websocket) must follow the same totals rule as the loaded list,
      // otherwise grouping rows would reappear while the list is price-ordered.
      return isDateOrdered(getApiQuery(currentQueryRef.current).order_by)
        ? expensesHelpers.getGroupedExpenses(combined)
        : combined;
    });
    scheduleTotalsRefresh();
  }, [scheduleTotalsRefresh]);

  const loadMore = useCallback(() => {
    if (!paginationRef.current?.cursor) return;
    loadExpenses({queryOverride: {cursor: paginationRef.current.cursor}});
  }, [loadExpenses]);

  const rowsWithTotals = useMemo(() => expensesHelpers.applyMonthlyTotals(rows, totals), [rows, totals]);

  return {
    rows: rowsWithTotals, loading, pagination, loaded, currentQuery,
    handleTableChange, updateRow, updateRows, refresh, filter, loadExpense, prependExpenses, loadMore,
  };
};
